import { api } from './client';
import type { OrganizationKind } from './organizations';

export const COUNTERPARTY_KIND_LABELS: Record<OrganizationKind, string> = {
  LEGAL_ENTITY: 'Юридическое лицо',
  ENTREPRENEUR: 'Индивидуальный предприниматель',
  INDIVIDUAL: 'Физическое лицо',
  FOREIGN: 'Иностранное лицо',
};

export interface CounterpartyBankAccount {
  id: string;
  counterpartyId: string;
  accountNumber: string;
  bankName: string;
  bik: string;
  corrAccount: string;
  isDefault: boolean;
}

export interface ContactPerson {
  id: string;
  counterpartyId: string;
  fullName: string;
  position: string | null;
  phone: string | null;
  email: string | null;
  comment: string | null;
}

export interface Counterparty {
  id: string;
  shortName: string;
  fullName: string;
  kind: OrganizationKind;
  inn: string | null;
  kpp: string | null;
  ogrn: string | null;
  okpo: string | null;
  addressFull: string | null;
  actualAddressFull: string | null;
  phone: string | null;
  email: string | null;
  comment: string | null;
  edoParticipantId: string | null;
  edoInviteStatus: string | null;
  isActive: boolean;
  bankAccounts: CounterpartyBankAccount[];
  contactPersons: ContactPerson[];
}

export interface CounterpartyInput {
  shortName: string;
  fullName: string;
  kind: OrganizationKind;
  inn?: string | null;
  kpp?: string | null;
  ogrn?: string | null;
  okpo?: string | null;
  addressFull?: string | null;
  actualAddressFull?: string | null;
  phone?: string | null;
  email?: string | null;
  comment?: string | null;
  edoParticipantId?: string | null;
  edoInviteStatus?: string | null;
}

export interface CounterpartyBankAccountInput {
  accountNumber: string;
  bankName: string;
  bik: string;
  corrAccount: string;
  isDefault?: boolean;
}

export interface ContactPersonInput {
  fullName: string;
  position?: string | null;
  phone?: string | null;
  email?: string | null;
  comment?: string | null;
}

export interface CounterpartyListResponse {
  items: Counterparty[];
  total: number;
  page: number;
  pageSize: number;
}

export interface CounterpartyListParams {
  search?: string;
  includeInactive?: boolean;
  page?: number;
  pageSize?: number;
}

function buildQuery(params: CounterpartyListParams): string {
  const search = new URLSearchParams();
  if (params.search) search.set('search', params.search);
  if (params.includeInactive) search.set('includeInactive', 'true');
  if (params.page !== undefined) search.set('page', String(params.page));
  if (params.pageSize !== undefined) search.set('pageSize', String(params.pageSize));
  const query = search.toString();
  return query ? `?${query}` : '';
}

export const counterpartiesApi = {
  list: (params: CounterpartyListParams = {}) =>
    api.get<CounterpartyListResponse>(`/counterparties${buildQuery(params)}`),

  get: (id: string) => api.get<{ counterparty: Counterparty }>(`/counterparties/${id}`),

  create: (data: CounterpartyInput) =>
    api.post<{ counterparty: Counterparty }>('/counterparties', data),

  update: (id: string, data: Partial<CounterpartyInput>) =>
    api.patch<{ counterparty: Counterparty }>(`/counterparties/${id}`, data),

  remove: (id: string) => api.delete<void>(`/counterparties/${id}`),

  activate: (id: string) => api.post<{ counterparty: Counterparty }>(`/counterparties/${id}/activate`),

  deactivate: (id: string) => api.post<{ counterparty: Counterparty }>(`/counterparties/${id}/deactivate`),

  addBankAccount: (id: string, data: CounterpartyBankAccountInput) =>
    api.post<{ bankAccount: CounterpartyBankAccount }>(`/counterparties/${id}/bank-accounts`, data),

  updateBankAccount: (id: string, accountId: string, data: Partial<CounterpartyBankAccountInput>) =>
    api.patch<{ bankAccount: CounterpartyBankAccount }>(
      `/counterparties/${id}/bank-accounts/${accountId}`,
      data,
    ),

  deleteBankAccount: (id: string, accountId: string) =>
    api.delete<void>(`/counterparties/${id}/bank-accounts/${accountId}`),

  addContactPerson: (id: string, data: ContactPersonInput) =>
    api.post<{ contactPerson: ContactPerson }>(`/counterparties/${id}/contact-persons`, data),

  updateContactPerson: (id: string, personId: string, data: Partial<ContactPersonInput>) =>
    api.patch<{ contactPerson: ContactPerson }>(
      `/counterparties/${id}/contact-persons/${personId}`,
      data,
    ),

  deleteContactPerson: (id: string, personId: string) =>
    api.delete<void>(`/counterparties/${id}/contact-persons/${personId}`),
};
